import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  backorderSampleFiles,
  fetchAllDocuments as fetchLeadDocuments,
} from "@src/store/slices/insurance";
import { addDocumentWeb } from "@src/store/slices/common";
import { docs_upload } from "@src/store/slices/commonId";
import ConfirmPopUp from "@src/common/ConfirmPopUp";
import FullPageLoader from "@src/common/FullPageLoader";
import secureStorage from "@src/config/encrypt";
import { toast } from "react-toastify";
import BackOrderImagePreviewPopup from "./BackOrderImagePreviewPopup";

const MAX_VEHICLE_PHOTOS = 10;
const BACK_ORDER_APPLICATION_DOC_ID = 22;

type UploadedPhoto = {
  id: string;
  name: string;
  file: File;
  previewUrl: string;
};

type AdminSampleImage = {
  id: string;
  title: string;
  label: string;
  imageUrl: string;
};

type ExistingBackOrderImage = {
  id: string;
  name: string;
  imageUrl: string;
};

type UploadedDocumentUrl = {
  url: string;
  document_id: number;
};

const getFileName = (path: string) => {
  const cleanPath = path?.split("?")?.[0] || "";
  return cleanPath.split("/").pop() || "Sample";
};

const getDocumentsFromResponse = (response: any) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.documents)) return response.data.documents;
  if (Array.isArray(response?.documents)) return response.documents;
  return [];
};

const normalizeSampleImages = (documents: any[]): AdminSampleImage[] => {
  return documents
    .map((document, index) => {
      const imageUrl = document?.signed_url || document?.doc_path || document?.document_path || document?.url || "";
      const title = document?.title || document?.label || document?.name || getFileName(imageUrl) || `Sample ${index + 1}`;

      if (!imageUrl) return null;

      return {
        id: String(document?.id || `${imageUrl}-${index}`),
        title,
        label: title?.toUpperCase(),
        imageUrl,
      };
    })
    .filter(Boolean) as AdminSampleImage[];
};

const normalizeBackOrderImages = (documents: any[]): ExistingBackOrderImage[] => {
  return documents
    .filter((document) => +document?.doc_id === BACK_ORDER_APPLICATION_DOC_ID)
    .map((document, index) => {
      const imageUrl = document?.path || document?.signed_url || document?.doc_path || document?.short_path || "";

      if (!imageUrl) return null;

      return {
        id: String(document?.id || `${imageUrl}-${index}`),
        name: getFileName(imageUrl),
        imageUrl,
      };
    })
    .filter(Boolean) as ExistingBackOrderImage[];
};

const buildBackOrderDocUrls = (uploadedFiles: any[]): UploadedDocumentUrl[] => {
  return uploadedFiles
    .map((uploadedFile) => {
      const fileUrl = uploadedFile?.file_url || uploadedFile?.signed_url || uploadedFile?.url || "";

      if (!fileUrl) return null;

      return {
        url: fileUrl,
        document_id: BACK_ORDER_APPLICATION_DOC_ID,
      };
    })
    .filter(Boolean) as UploadedDocumentUrl[];
};

export default function BackOrderDocUpload() {
  const { leadId } = useParams<{ leadId: string }>();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isSampleModalOpen, setIsSampleModalOpen] = useState(false);
  const [activeSampleIndex, setActiveSampleIndex] = useState(0);
  const [selectedSampleImage, setSelectedSampleImage] = useState<AdminSampleImage | null>(null);
  const [sampleImages, setSampleImages] = useState<AdminSampleImage[]>([]);
  const [backOrderDescription, setBackOrderDescription] = useState("");
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [isSampleLoading, setIsSampleLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sampleError, setSampleError] = useState("");
  const [uploadedPhotos, setUploadedPhotos] = useState<UploadedPhoto[]>([]);
  const [existingBackOrderImages, setExistingBackOrderImages] = useState<ExistingBackOrderImage[]>([]);
  const [isDocumentLoading, setIsDocumentLoading] = useState(false);
  const [documentError, setDocumentError] = useState("");
  const [uploadError, setUploadError] = useState("");
  const uploadedPhotosRef = useRef<UploadedPhoto[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const activeSample = sampleImages[activeSampleIndex];
  const hasSampleImages = sampleImages.length > 0;
  const existingPhotoCount = existingBackOrderImages.length;
  const totalPhotoCount = existingPhotoCount + uploadedPhotos.length;
  const hasUploadedBackOrderImages = existingPhotoCount > 0;
  const isMaxUploaded = existingPhotoCount >= MAX_VEHICLE_PHOTOS;
  const canUploadBackOrderImages = !hasUploadedBackOrderImages && totalPhotoCount < MAX_VEHICLE_PHOTOS;
  const validationMessage =
    uploadError ||
    documentError;
  const isUploadStatusSuccess = !validationMessage && hasUploadedBackOrderImages;
  const uploadStatusMessage =
    validationMessage ||
    (isUploadStatusSuccess
      ? `${existingPhotoCount} image${existingPhotoCount > 1 ? "s" : ""} uploaded successfully`
      : "Upload Images according to the sample");
  const uploadStatusIcon = isUploadStatusSuccess ? "ic-check" : "ic-warning";
  const isSubmitDisabled =
    isSubmitting || isDocumentLoading || hasUploadedBackOrderImages || isMaxUploaded || !uploadedPhotos.length;

  useEffect(() => {
    uploadedPhotosRef.current = uploadedPhotos;
  }, [uploadedPhotos]);

  useEffect(() => {
    return () => {
      uploadedPhotosRef.current.forEach((photo) => URL.revokeObjectURL(photo.previewUrl));
    };
  }, []);

  const toggleAccordion = () => {
    setIsExpanded((current) => !current);
  };

  const handleAccordionKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggleAccordion();
    }
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const fetchSampleImages = useCallback(async () => {
    const numericLeadId = Number(leadId);

    if (!numericLeadId) {
      setSampleImages([]);
      setBackOrderDescription("");
      setSampleError("Invalid lead id");
      return;
    }

    setIsSampleLoading(true);
    setSampleError("");

    try {
      const response: any = await backorderSampleFiles({ lead_id: numericLeadId });

      if (+response?.status !== 200) {
        setActiveSampleIndex(0);
        setSampleImages([]);
        setBackOrderDescription("");
        setSampleError(response?.message || response?.errors || "Unable to load sample images");
        return;
      }

      const documents = Array.isArray(response?.data?.documents) ? response.data.documents : [];
      const mappedImages = normalizeSampleImages(documents);
      const description = response?.data?.back_order_desc || "";

      setActiveSampleIndex(0);
      setSampleImages(mappedImages);
      setBackOrderDescription(description);
      setSampleError(mappedImages.length ? "" : "No sample images found");
    } catch (error: any) {
      setSampleImages([]);
      setBackOrderDescription("");
      setSampleError(error?.message || "Unable to load sample images");
    } finally {
      setIsSampleLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    void fetchSampleImages();
  }, [fetchSampleImages]);

  const fetchUploadedDocuments = useCallback(async () => {
    const numericLeadId = Number(leadId);

    if (!numericLeadId) {
      setExistingBackOrderImages([]);
      setDocumentError("Invalid lead id");
      return;
    }

    setIsDocumentLoading(true);
    setDocumentError("");

    try {
      const response: any = await fetchLeadDocuments({ lead_id: numericLeadId });

      if (+response?.status !== 200) {
        setExistingBackOrderImages([]);
        setDocumentError(response?.message || response?.errors || "Unable to load uploaded images");
        return;
      }

      setExistingBackOrderImages(normalizeBackOrderImages(getDocumentsFromResponse(response)));
    } catch (error: any) {
      setExistingBackOrderImages([]);
      setDocumentError(error?.message || "Unable to load uploaded images");
    } finally {
      setIsDocumentLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    void fetchUploadedDocuments();
  }, [fetchUploadedDocuments]);

  const openSampleModal = () => {
    setIsSampleModalOpen(true);
  };

  const closeSampleModal = () => {
    setSelectedSampleImage(null);
    setIsSampleModalOpen(false);
  };

  const openLargeSamplePreview = (sample: AdminSampleImage) => {
    setSelectedSampleImage(sample);
  };

  const closeLargeSamplePreview = () => {
    setSelectedSampleImage(null);
  };

  const handleBackClick = () => {
    navigate("/dashboard");
  };

  const showPreviousSample = () => {
    setActiveSampleIndex((currentIndex) =>
      currentIndex === 0 ? sampleImages.length - 1 : currentIndex - 1,
    );
  };

  const showNextSample = () => {
    setActiveSampleIndex((currentIndex) =>
      currentIndex === sampleImages.length - 1 ? 0 : currentIndex + 1,
    );
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);

    if (!selectedFiles.length) return;

    setUploadedPhotos((currentPhotos) => {
      const remainingSlots = MAX_VEHICLE_PHOTOS - existingBackOrderImages.length - currentPhotos.length;

      if (remainingSlots <= 0) {
        setUploadError(`You can upload maximum ${MAX_VEHICLE_PHOTOS} images`);
        return currentPhotos;
      }

      if (selectedFiles.length > remainingSlots) {
        setUploadError(`You can upload maximum ${MAX_VEHICLE_PHOTOS} images`);
        return currentPhotos;
      }

      setUploadError("");

      const nextPhotos = selectedFiles.slice(0, remainingSlots).map((file, index) => ({
        id: `${file.name}-${file.lastModified}-${Date.now()}-${index}`,
        name: file.name,
        file,
        previewUrl: URL.createObjectURL(file),
      }));

      return [...currentPhotos, ...nextPhotos];
    });

    event.target.value = "";
  };

  const removePhoto = (photoId: string) => {
    setUploadError("");

    setUploadedPhotos((currentPhotos) => {
      const removedPhoto = currentPhotos.find((photo) => photo.id === photoId);

      if (removedPhoto) {
        URL.revokeObjectURL(removedPhoto.previewUrl);
      }

      return currentPhotos.filter((photo) => photo.id !== photoId);
    });
  };

  const validateSubmit = () => {
    const numericLeadId = Number(leadId);

    if (isSubmitting) return false;

    if (!numericLeadId) {
      setUploadError("Invalid lead id");
      return false;
    }

    if (hasUploadedBackOrderImages) {
      setUploadError("Document already uploaded");
      return false;
    }

    if (existingBackOrderImages.length >= MAX_VEHICLE_PHOTOS) {
      setUploadError(`You can upload maximum ${MAX_VEHICLE_PHOTOS} images`);
      return false;
    }

    if (!uploadedPhotos.length) {
      setUploadError("Please upload at least one image");
      return false;
    }

    if (existingBackOrderImages.length + uploadedPhotos.length > MAX_VEHICLE_PHOTOS) {
      setUploadError(`You can upload maximum ${MAX_VEHICLE_PHOTOS} images`);
      return false;
    }

    setUploadError("");
    return true;
  };

  const openSubmitConfirm = () => {
    if (validateSubmit()) setShowSubmitConfirm(true);
  };

  const closeSubmitConfirm = () => {
    setShowSubmitConfirm(false);
  };

  const handleSubmit = async () => {
    const numericLeadId = Number(leadId);

    if (!validateSubmit()) return;

    const formData = new FormData();
    formData.append("upload_type", "insurance_docs");
    formData.append("lead_id", `${numericLeadId}`);
    uploadedPhotos.forEach((photo) => formData.append("images", photo.file));

    setIsSubmitting(true);
    setShowSubmitConfirm(false);

    try {
      const uploadResponse: any = await docs_upload(formData);

      if (+uploadResponse?.status !== 200) {
        setUploadError(uploadResponse?.message || uploadResponse?.errors || "Unable to upload images");
        return;
      }

      const docUrls = buildBackOrderDocUrls(uploadResponse?.data || []);

      if (docUrls.length !== uploadedPhotos.length) {
        setUploadError("Unable to upload images");
        return;
      }

      const { user_data } = secureStorage.getItem("authAgentUserInfo") || {};
      const addDocumentResponse: any = await addDocumentWeb({
        lead_id: numericLeadId,
        doc_path: docUrls,
        is_resubmit: true,
        created_by: user_data?.id,
        created_by_name: user_data?.name,
      });

      if (+addDocumentResponse?.status !== 200) {
        setUploadError(addDocumentResponse?.message || addDocumentResponse?.errors || "Unable to save documents");
        return;
      }

      setUploadedPhotos((currentPhotos) => {
        currentPhotos.forEach((photo) => URL.revokeObjectURL(photo.previewUrl));
        return [];
      });
      await fetchUploadedDocuments();
      toast.success("Back Order Images Uploaded Successfully");
    } catch (error) {
      setUploadError("Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container-fluid back-order-upload-page">
      <FullPageLoader show={isSampleLoading || isSubmitting || isDocumentLoading} />

      <div className="top-heading financer-lead-heding insurance-top-filer-heading d-flex back-order-page-heading">
        <div className="back-order-title-wrap">
          <button type="button" className="back-order-back-btn" aria-label="Back" onClick={handleBackClick}>
            <i className="ic-arrow_back"></i>
          </button>
          <h1>Document Resubmission</h1>
        </div>
      </div>

      <div className={`back-order-doc-card ${!isExpanded ? "collapsed" : ""}`}>
        <div
          className="back-order-doc-header"
          role="button"
          tabIndex={0}
          aria-expanded={isExpanded}
          onClick={toggleAccordion}
          onKeyDown={handleAccordionKeyDown}
        >
          <div className="back-order-doc-title">
            <span className="back-order-doc-icon">
              <i className="ic-directions_car"></i>
            </span>
            <span>
              <strong>Vehicle Photos</strong>
              {isExpanded && (
                <button
                  type="button"
                  className="back-order-sample-link"
                  onClick={(event) => {
                    event.stopPropagation();
                    openSampleModal();
                  }}
                >
                  View Sample
                </button>
              )}
            </span>
          </div>

          <div className="back-order-doc-actions">
            {isExpanded && (
              <span className="back-order-status">
                <i className="ic-check"></i>
              </span>
            )}
            <i className={isExpanded ? "ic-arrow_drop_up" : "ic-arrow_drop_down"}></i>
          </div>
        </div>

        <div className="back-order-alert">
          <i className="ic-warning"></i>
          <span>
            {isExpanded ? (
              <>
                <span className="back-order-alert-title">Resubmission Required</span>
                <span>: Please Re-Upload Vehicle Photos</span>
              </>
            ) : (
              "Resubmission required"
            )}
          </span>
        </div>

        {isExpanded && (
          <>
            {!!backOrderDescription && (
              <div className="back-order-remarks-box">
                <span>REMARKS FROM OPERATIONS</span>
                <p>{backOrderDescription}</p>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={handleFileChange}
            />

            <div className="back-order-upload-row">
              {existingBackOrderImages.map((photo) => (
                <div className="back-order-preview-tile" key={photo.id}>
                  <img src={photo.imageUrl} alt={photo.name} />
                  <span className="back-order-file-name">{photo.name}</span>
                </div>
              ))}

              {uploadedPhotos.map((photo) => (
                <div className="back-order-preview-tile" key={photo.id}>
                  <button
                    type="button"
                    className="back-order-remove-btn"
                    aria-label={`Remove ${photo.name}`}
                    onClick={() => removePhoto(photo.id)}
                  >
                    <i className="ic-clearclose"></i>
                  </button>
                  <img src={photo.previewUrl} alt={photo.name} />
                  <span className="back-order-file-name">{photo.name}</span>
                </div>
              ))}

              {canUploadBackOrderImages && (
                <button type="button" className="back-order-upload-tile" onClick={openFilePicker}>
                  <i className="ic-add"></i>
                  <span>Upload Photo</span>
                </button>
              )}
            </div>

            <p className={`back-order-sample-instruction ${isUploadStatusSuccess ? "success" : "danger"}`}>
              <i className={uploadStatusIcon}></i>
              <span>{uploadStatusMessage}</span>
            </p>
          </>
        )}
      </div>

      {isExpanded && (
        <div className="back-order-submit-wrap">
          <button
            type="button"
            className="btn-primary back-order-submit-btn"
            disabled={isSubmitDisabled}
            onClick={openSubmitConfirm}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      )}

      {showSubmitConfirm && (
        <ConfirmPopUp
          title="Confirm Submit"
          message={`Are you sure you want to submit ${uploadedPhotos.length} image${uploadedPhotos.length > 1 ? "s" : ""}?`}
          confirmNo={closeSubmitConfirm}
          confirmYes={handleSubmit}
        />
      )}

      {isSampleModalOpen && (
        <div className="back-order-sample-modal-overlay">
          <div className="back-order-sample-modal" role="dialog" aria-modal="true">
            <button type="button" className="back-order-sample-close" onClick={closeSampleModal}>
              <i className="ic-clearclose"></i>
            </button>

            <h2>Vehicle Images</h2>
            <p>Please upload the images as requested by insurer</p>

            <div className={`back-order-sample-carousel ${!hasSampleImages && !isSampleLoading ? "empty" : ""}`}>
              {hasSampleImages && (
                <button
                  type="button"
                  className="back-order-sample-nav prev"
                  onClick={showPreviousSample}
                  disabled={sampleImages.length < 2 || isSampleLoading}
                >
                  <i className="ic-arrow_back_ios"></i>
                </button>
              )}

              <div className="back-order-sample-frame">
                {isSampleLoading ? (
                  <div className="back-order-sample-placeholder">
                    <span>SAMPLE</span>
                    <strong>Loading</strong>
                    <small>PLEASE WAIT</small>
                  </div>
                ) : activeSample?.imageUrl ? (
                  <button
                    type="button"
                    className="back-order-sample-image-button"
                    aria-label={`View ${activeSample.title} in large size`}
                    onClick={() => openLargeSamplePreview(activeSample)}
                  >
                    <img src={activeSample.imageUrl} alt={activeSample.title} />
                  </button>
                ) : (
                  <div className="back-order-sample-placeholder">
                    <span>SAMPLE</span>
                    <strong>{sampleError || "No Images"}</strong>
                    <small>BACK ORDER</small>
                  </div>
                )}
              </div>

              {hasSampleImages && (
                <button
                  type="button"
                  className="back-order-sample-nav next"
                  onClick={showNextSample}
                  disabled={sampleImages.length < 2 || isSampleLoading}
                >
                  <i className="ic-arrow_forward_ios"></i>
                </button>
              )}
            </div>

            {!!sampleImages.length && (
              <div className="back-order-sample-dots">
                {sampleImages.map((sample, index) => (
                  <button
                    type="button"
                    key={sample.id}
                    className={index === activeSampleIndex ? "active" : ""}
                    aria-label={`Show ${sample.title}`}
                    onClick={() => setActiveSampleIndex(index)}
                  />
                ))}
              </div>
            )}

            <button type="button" className="back-order-sample-close-btn" onClick={closeSampleModal}>
              Close
            </button>
          </div>
        </div>
      )}

      {selectedSampleImage && (
        <BackOrderImagePreviewPopup
          imageUrl={selectedSampleImage.imageUrl}
          title={selectedSampleImage.title}
          onClose={closeLargeSamplePreview}
        />
      )}
    </div>
  );
}
